import { Component, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import { Spinkit } from 'ng-http-loader';
import { ToastrService } from 'ngx-toastr';
import { HttpService } from 'src/app/services/http/http.service';

@Component({
  selector: 'app-regulatory-and-event-plan',
  templateUrl: './regulatory-and-event-plan.component.html',
  styleUrls: ['./regulatory-and-event-plan.component.scss']
})
export class RegulatoryAndEventPlanComponent implements OnInit {
  public spinkit = Spinkit;

  infoFlag = true;
  calendarFlag = false;

  activeTab = 'information';

  form!: FormGroup;
  calendarForm!: FormGroup;
  menuPlanningForm!: FormGroup;

  savingIndex: number | null = null;
  savingMonthKey: string | null = null;
  menuPlanningSavingIndex: number | null = null;

  selectedYearIndex = 0;
  selectedMonthIndex = 0;

  monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  constructor(private fb: FormBuilder, private httpSv: HttpService, private toastr: ToastrService) { }

  ngOnInit(): void {
    this.initInfoForm();
    this.initCalendarForm();
    this.getRegulatoryInfo();
    this.initMenuPlanningForm();
  }


  setActiveTab(tab: string): void {
    if (this.activeTab === tab) return
    this.activeTab = tab;
    switch (tab) {
      case 'information':
        this.regulatoryInfo();
        break;
      case 'calendar':
        this.eventPlan();
        break;
      case 'menuPlanning':
        this.menuPlanning();
        break;
    }
  }


  regulatoryInfo(): void {
    if (this.infoFlag) return
    this.infoFlag = true;
    this.calendarFlag = false;
    this.getRegulatoryInfo();
  }

  eventPlan(): void {
    if (this.calendarFlag) return
    this.infoFlag = false;
    this.calendarFlag = true;
    this.getEventPlan();
  }



  // ------------------------------------------- INFO ----------------------------------------------------
  initInfoForm(): void {
    this.form = this.fb.group({
      cards: this.fb.array([])
    });
  }

  get cards(): FormArray {
    return this.form.get('cards') as FormArray;
  }

  createCard(data?: { id?: any, title?: string; descriptions?: string }): FormGroup {
    return this.fb.group({
      id: [data?.id || null],
      title: [data?.title || '', Validators.required],
      descriptions: [data?.descriptions || '', Validators.required]
    });
  }

  addCard(data?: { title?: string; descriptions?: string }): void {
    this.cards.push(this.createCard(data));
  }

  removeCard(index: number): void {
    const card = this.cards.at(index) as FormGroup;

    if (card.get('id')?.value) {
      this.httpSv.deleteRegulatoryInfo(card.get('id')?.value).subscribe((res: any) => {
        if (res['status']) {
          this.toastr.success('Deleted successfully', '', { timeOut: 2000 });
          this.cards.removeAt(index);
        }
      }, (err: any) => {
        this.toastr.error('Opps! Something went wrong', '', { timeOut: 2000 });
      })
    } else {
      this.cards.removeAt(index);
    }

  }

  getRegulatoryInfo(): void {
    this.httpSv.getRegulatoryInfo().subscribe((res: any) => {
      if (res['status']) {
        this.cards.clear();
        res['result']['data'].forEach((card: any) => {
          this.addCard(card);
        });
      }
    }, (err: any) => {
      this.toastr.error('Opps! Something went wrong', '', { timeOut: 2000 });
    })
  }


  saveCard(index: number): void {
    const card = this.cards.at(index) as FormGroup;

    if (card.invalid) {
      card.markAllAsTouched();
      return;
    }

    this.savingIndex = index;
    const payload = card.value;
    this.httpSv.createUpdateRegulatoryInfo(payload).subscribe((res: any) => {
      if (res['status']) {
        this.toastr.success('Saved successfully', '', { timeOut: 2000 });
        if (payload.id == null) {
          card.get('id')?.setValue(res['result']['data']['id']);
        }
        this.savingIndex = null;
      }
      else {
        this.toastr.error(res['message'], '', { timeOut: 2000 });
      }
    }, (err: any) => {
      this.toastr.error('Opps! Something went wrong', '', { timeOut: 2000 });
    })
  }

  // ---------------- CALENDAR ------------------------------------------------------
  eventTypes: string[] = ['holiday', 'season', 'event', 'deadline'];
  savingEventKey: string | null = null;
  isSavingEvent(eventIndex: number): boolean {
    return this.savingEventKey ===
      this.selectedYearIndex + '-' + this.selectedMonthIndex + '-' + eventIndex;
  }

  initCalendarForm(): void {
    const currentYear = new Date().getFullYear();
    this.calendarForm = this.fb.group({
      years: this.fb.array([
        this.createYear(currentYear),
        this.createYear(currentYear + 1)
      ])
    });
  }

  get years(): FormArray {
    return this.calendarForm.get('years') as FormArray;
  }

  createYear(year: number): FormGroup {
    return this.fb.group({
      year: [year],
      months: this.fb.array(
        this.monthNames.map((monthName, index) =>
          this.fb.group({
            monthName: [monthName],
            monthNumber: [index + 1],
            events: this.fb.array([])
          })
        )
      )
    });
  }

  createEvent(year: number, monthNumber: number, data?: any): FormGroup {
    return this.fb.group({
      id: [data?.id || null],
      eventDate: [data?.date || data?.eventDate || this.getDefaultDate(year, monthNumber), Validators.required],
      type: [data?.type || 'event', Validators.required],
      title: [data?.event || data?.title || '', Validators.required],
      descriptions: [data?.descriptions || data?.description || '']
    });
  }

  clearCalendarEvents(): void {
    for (let yi = 0; yi < this.years.length; yi++) {
      const months = this.getMonths(yi);

      for (let mi = 0; mi < months.length; mi++) {
        const events = this.getEvents(yi, mi);
        events.clear();
      }
    }
  }

  getYearIndexByValue(year: number): number {
    for (let i = 0; i < this.years.length; i++) {
      if (this.getYearValue(i) === year) {
        return i;
      }
    }
    return -1;
  }

  setCalendarData(apiData: any[]): void {
    if (!apiData || !apiData.length) {
      this.clearCalendarEvents();
      return;
    }

    this.clearCalendarEvents();

    apiData.forEach((item: any) => {
      if (!item?.date) {
        return;
      }

      const parsedDate = new Date(item.date);

      if (isNaN(parsedDate.getTime())) {
        return;
      }

      const year = parsedDate.getFullYear();
      const monthIndex = parsedDate.getMonth(); // 0 to 11

      const yearIndex = this.getYearIndexByValue(year);

      // only load if year exists in current form
      if (yearIndex === -1) {
        return;
      }

      this.getEvents(yearIndex, monthIndex).push(
        this.createEvent(year, monthIndex + 1, item)
      );
    });
  }

  getYearValue(yearIndex: number): number {
    return (this.years.at(yearIndex) as FormGroup).get('year')?.value;
  }

  getMonths(yearIndex: number): FormArray {
    return ((this.years.at(yearIndex) as FormGroup).get('months')) as FormArray;
  }

  getEvents(yearIndex: number, monthIndex: number): FormArray {
    return ((this.getMonths(yearIndex).at(monthIndex) as FormGroup).get('events')) as FormArray;
  }

  selectYear(index: number): void {
    this.selectedYearIndex = index;
    this.selectedMonthIndex = 0;
  }

  selectMonth(index: number): void {
    this.selectedMonthIndex = index;
  }

  addEvent(yearIndex: number, monthIndex: number, data?: any): void {
    const year = this.getYearValue(yearIndex);
    const monthNumber = (this.getMonths(yearIndex).at(monthIndex) as FormGroup).get('monthNumber')?.value;
    this.getEvents(yearIndex, monthIndex).push(this.createEvent(year, monthNumber, data));
  }

  addEventToSelectedMonth(): void {
    this.addEvent(this.selectedYearIndex, this.selectedMonthIndex);
  }

  removeEvent(eventIndex: number): void {
    const eventGroup = this.getSelectedEvents().at(eventIndex) as FormGroup;
    const id = eventGroup.get('id')?.value;
    if (id) {
      this.httpSv.deleteEventPlan(id).subscribe((res: any) => {
        if (res['status']) {
          this.toastr.success('Deleted successfully', '', { timeOut: 2000 });
          this.getEvents(this.selectedYearIndex, this.selectedMonthIndex).removeAt(eventIndex);
        }
      }, (err: any) => {
        this.toastr.error('Opps! Something went wrong', '', { timeOut: 2000 });
      });
    }
    else {
      this.getEvents(this.selectedYearIndex, this.selectedMonthIndex).removeAt(eventIndex);
    }
  }

  saveSelectedMonth(): void {
    const yearIndex = this.selectedYearIndex;
    const monthIndex = this.selectedMonthIndex;

    const monthGroup = this.getMonths(yearIndex).at(monthIndex) as FormGroup;
    const events = this.getEvents(yearIndex, monthIndex);

    if (events.length === 0) {
      alert('Please add at least one event');
      return;
    }

    if (events.invalid) {
      events.markAllAsTouched();
      return;
    }

    const monthKey = `${yearIndex}-${monthIndex}`;
    this.savingMonthKey = monthKey;

    const payload = {
      year: this.getYearValue(yearIndex),
      monthNumber: monthGroup.get('monthNumber')?.value,
      monthName: monthGroup.get('monthName')?.value,
      events: events.value
    };

    console.log('Saving month payload:', payload);

    setTimeout(() => {
      this.savingMonthKey = null;
      alert(`${monthGroup.get('monthName')?.value} saved successfully`);
    }, 800);
  }

  getEventPlan() {
    this.httpSv.getEventPlan().subscribe((res: any) => {
      if (res['status']) {
        this.setCalendarData(res['result']['data']);
      }
    }, (err: any) => {
      console.log(err);
      this.toastr.error('Opps! Something went wrong', '', { timeOut: 2000 });
    })
  }

  saveEvent(eventIndex: number): void {
    const yearIndex = this.selectedYearIndex;
    const monthIndex = this.selectedMonthIndex;

    const monthGroup = this.getMonths(yearIndex).at(monthIndex) as FormGroup;
    const eventGroup = this.getSelectedEvents().at(eventIndex) as FormGroup;

    if (eventGroup.invalid) {
      eventGroup.markAllAsTouched();
      return;
    }

    const saveKey = `${yearIndex}-${monthIndex}-${eventIndex}`;
    this.savingEventKey = saveKey;

    const payload = {
      id: eventGroup.get('id')?.value || null,
      event: eventGroup.get('title')?.value,
      descriptions: eventGroup.get('descriptions')?.value,
      date: eventGroup.get('eventDate')?.value,
      type: eventGroup.get('type')?.value
    };

    this.httpSv.createUpdateEventPlan(payload).subscribe((res: any) => {
      if (res['status']) {
        this.toastr.success('Saved successfully', '', { timeOut: 2000 });
        if (payload.id == null) {
          eventGroup.get('id')?.setValue(res['result']['data']['id']);
        }
        this.savingEventKey = null;
      }
    }, (err: any) => {
      this.savingEventKey = null;
      console.log(err);
      this.toastr.error('Opps! Something went wrong', '', { timeOut: 2000 });
    })
  }

  getSelectedMonthGroup(): FormGroup {
    return this.getMonths(this.selectedYearIndex).at(this.selectedMonthIndex) as FormGroup;
  }

  getSelectedEvents(): FormArray {
    return this.getEvents(this.selectedYearIndex, this.selectedMonthIndex);
  }

  getSelectedMonthName(): string {
    return this.getSelectedMonthGroup().get('monthName')?.value;
  }

  getSelectedMonthNumber(): number {
    return this.getSelectedMonthGroup().get('monthNumber')?.value;
  }

  getEventCount(yearIndex: number, monthIndex: number): number {
    return this.getEvents(yearIndex, monthIndex).length;
  }

  getDefaultDate(year: number, monthNumber: number): string {
    return `${year}-${this.pad(monthNumber)}-01`;
  }

  getSelectedMonthMinDate(): string {
    const year = this.getYearValue(this.selectedYearIndex);
    const monthNumber = this.getSelectedMonthNumber();
    return `${year}-${this.pad(monthNumber)}-01`;
  }

  getSelectedMonthMaxDate(): string {
    const year = this.getYearValue(this.selectedYearIndex);
    const monthNumber = this.getSelectedMonthNumber();
    const lastDay = new Date(year, monthNumber, 0).getDate();
    return `${year}-${this.pad(monthNumber)}-${this.pad(lastDay)}`;
  }

  pad(value: number): string {
    return value < 10 ? '0' + value : value.toString();
  }



  // Menu Planning-------------------------------------------------------------
  initMenuPlanningForm(): void {
    this.menuPlanningForm = this.fb.group({
      resources: this.fb.array([])
    });
  }

  get menuPlanningCards(): FormArray {
    return this.menuPlanningForm.get('resources') as FormArray;
  }

  createMenuPlanningCard(data?: any): FormGroup {
    return this.fb.group({
      id: [data?.id || null],
      title: [data?.title || '', Validators.required],
      pointsText: [
        data?.points ? data.points.join('\n') : '',
        Validators.required
      ]
    });
  }

  addMenuPlanningCard(data?: any): void {
    this.menuPlanningCards.push(this.createMenuPlanningCard(data));
  }

  removeMenuPlanningCard(index: number): void {
    const card = this.menuPlanningCards.at(index) as FormGroup;
    const id = card.get('id')?.value || null
    if (id) {
      this.httpSv.deleteMenuPlanning(id).subscribe((res: any) => {
        if (res['status']) {
          this.toastr.success('Deleted successfully', '', { timeOut: 2000 });
          this.menuPlanningCards.removeAt(index);
        }
      }, (err: any) => {
        console.log(err);
        this.toastr.error('Opps! Something went wrong', '', { timeOut: 2000 });
      })
    } else {
      this.menuPlanningCards.removeAt(index);
    }

  }

  saveMenuPlanningCard(index: number): void {
    const card = this.menuPlanningCards.at(index) as FormGroup;

    if (card.invalid) {
      card.markAllAsTouched();
      return;
    }

    this.menuPlanningSavingIndex = index;

    const payload = {
      id: card.get('id')?.value || null,
      title: card.get('title')?.value,
      points: (card.get('pointsText')?.value || '')
        .split('\n')
        .map((item: string) => item.trim())
        .filter((item: string) => item)
    };

    this.httpSv.createUpdateMenuPlanning(payload).subscribe((res: any) => {
      if (res['status']) {
        this.toastr.success('Saved successfully', '', { timeOut: 2000 });
        if (payload.id == null) {
          card.get('id')?.setValue(res['result']['data']['id']);
        }
        this.menuPlanningSavingIndex = null;
      }
    }, (err: any) => {
      this.menuPlanningSavingIndex = null;
      console.log(err);
      this.toastr.error('Opps! Something went wrong', '', { timeOut: 2000 });
    })

  }


  menuPlanning(): void {
    this.httpSv.getMenuPlanning().subscribe((res: any) => {
      if (res['status']) {
        this.menuPlanningCards.clear();
        res['result']['data'].forEach((card: any) => {
          this.addMenuPlanningCard(card);
        });
      }
    }, (err: any) => {
      this.toastr.error('Opps! Something went wrong', '', { timeOut: 2000 });
    })
  }
}